import React, { useEffect, useState } from 'react';

import { ButtonIconOnly, FlexContainer, InfoIcon, InformationBox, PlusIcon, SearchFilter, SidebarDialog, Spacing } from '@netfront/ui-library';

import { UserGroupAddContact } from './UserGroupAddContact';
import { UserGroupContactForm } from './UserGroupContactForm';
import { getGroupContactItems } from './UserGroupContactsTab.helpers';
import { UserGroupContactsTabProps, IContact,  } from './UserGroupContactsTab.interfaces';

import { SidebarContainer, SidebarList, SidebarListItem } from '../../../../components/Shared';

const UserGroupContactsTab = ({ selectedGroupId, allGroupContacts = [], onUpdateContact }: UserGroupContactsTabProps) => {
  const [filteredGroupContacts, setFilteredGroupContacts] = useState<IContact[]>([]);
  const [groupContacts, setGroupContacts] = useState<IContact[]>([]);
  const [groupContact, setGroupContact] = useState<IContact | undefined>(undefined);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState<boolean>(false);
  const [isUpdateContactDialogOpen, setIsUpdateContactDialogOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleClickClearSearch = () => {
    setSearchValue('');
    setFilteredGroupContacts(groupContacts);
  };

  const handleSearchInputChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(value);
  };

  // Add contact
  const handleAddContact = () => {
    setIsAddContactDialogOpen(true);
  };

  const handleCancelAddContact = () => {
    setIsAddContactDialogOpen(false);
    handleClickClearSearch();
  };
  
  // Update contact
  const handleUpdateContact = (contactId: number) => {
    setIsUpdateContactDialogOpen(true);
    setGroupContact(groupContacts.find((item) => item.id === contactId));
  };

  const handleCancelUpdateContact = () => {
    setIsUpdateContactDialogOpen(false);
    setGroupContact(undefined);
    handleClickClearSearch();
  };

  useEffect(() => {
    const filteredSearchList = groupContacts
      .filter(
        ({ firstname, lastname, email }) =>
          firstname.toLocaleLowerCase().trim().includes(searchValue.trim().toLocaleLowerCase()) ||
          lastname.toLocaleLowerCase().trim().includes(searchValue.trim().toLocaleLowerCase()) ||
          email.toLocaleLowerCase().trim().includes(searchValue.trim().toLocaleLowerCase()),
      )
      .sort((a, b) => a.firstname.localeCompare(b.firstname));

    setFilteredGroupContacts(filteredSearchList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);


  useEffect(() => {
    if (!selectedGroupId || allGroupContacts.length === 0) {
      return;
    }

    setGroupContacts(getGroupContactItems(allGroupContacts, selectedGroupId));
    setFilteredGroupContacts(getGroupContactItems(allGroupContacts, selectedGroupId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, allGroupContacts]);

  return (
    <SidebarContainer>
      <>
        {!filteredGroupContacts.length ? (
          <Spacing>
            <InformationBox icon={InfoIcon}  message="There are no contacts attached to this group" />
          </Spacing>
        ) : (
          <>
            <Spacing>
              <section>
                <SearchFilter
                  hasSearchButton={false}
                  id="searchContacts"
                  isLabelHidden={true}
                  labelText="Search contacts"
                  name="searchContacts"
                  placeholder="Filter name, email"
                  type="text"
                  value={searchValue}
                  isFullWidth
                  isLabelSideBySide
                  onChange={handleSearchInputChange}
                  onClear={handleClickClearSearch}
                  onSearch={() => {
                    return;
                  }}
                />
              </section>
            </Spacing>

            <section>
              <SidebarList itemsLength={filteredGroupContacts.length} text="contact" hasCount>
                {filteredGroupContacts.map(({ id, firstname, lastname }) => (
                  <SidebarListItem
                    key={id}
                    name={`${firstname} ${lastname}`}
                    supportiveText="Update the contact"
                    title={`${firstname} ${lastname}`}
                    isSettingsButton
                    onClick={() => handleUpdateContact(id)}
                  />
                ))}
              </SidebarList>
            </section>
          </>
        )}

        <FlexContainer justifyContent="end" tag="section">
          <ButtonIconOnly icon={PlusIcon} text="Add contact" onClick={handleAddContact} />
        </FlexContainer>
      </>

      <SidebarDialog
        isOpen={isUpdateContactDialogOpen}
        onClose={handleCancelUpdateContact}
      >
        {isUpdateContactDialogOpen && groupContact && (
          <UserGroupContactForm
            contact={groupContact}
            onOpenUpdateContacts={handleCancelUpdateContact}
            onUpdateContacts={onUpdateContact}
          />
        )}
      </SidebarDialog>
      
      <SidebarDialog
        isOpen={isAddContactDialogOpen}
        onClose={handleCancelAddContact}
      >
        {isAddContactDialogOpen && (
          <UserGroupAddContact
            contactGroupId={selectedGroupId}
            onOpenUpdateContacts={handleCancelAddContact}
            onUpdateContacts={onUpdateContact}
          />
        )}
      </SidebarDialog>
    </SidebarContainer>
  );
};

export { UserGroupContactsTab };
